/**
 * Chip primitives for the redesigned Tasks UI.
 *
 * Each chip has two modes:
 *   - Static: displays the value with the design's OKLCH color tokens.
 *   - Editable: when `onChange` is supplied, wraps the chip in a shadcn
 *     Select so the user can inline-edit the value. The trigger is
 *     visually transparent so the chip itself carries the whole hit area.
 */
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// ── Status ──────────────────────────────────────────────────────────

export type TaskStatus =
  | "not_started"
  | "in_progress"
  | "in_review"
  | "completed"
  | "blocked"
  | "skipped"
  | "overdue";

const STATUS_LABELS: Record<TaskStatus, string> = {
  not_started: "Todo",
  in_progress: "In Progress",
  in_review: "In Review",
  completed: "Done",
  blocked: "Blocked",
  skipped: "Skipped",
  overdue: "Overdue",
};

const STATUS_CLS: Record<TaskStatus, string> = {
  not_started: "s-todo",
  in_progress: "s-progress",
  in_review: "s-review",
  completed: "s-done",
  blocked: "s-blocked",
  skipped: "s-todo",
  overdue: "s-blocked",
};

const STATUS_OPTIONS: TaskStatus[] = [
  "not_started",
  "in_progress",
  "in_review",
  "completed",
  "blocked",
];

export function StatusChip({
  value,
  onChange,
}: {
  value: TaskStatus | string | null;
  onChange?: (next: TaskStatus) => void;
}) {
  const v = (value ?? "not_started") as TaskStatus;
  const cls = STATUS_CLS[v] ?? "s-todo";
  const label = STATUS_LABELS[v] ?? v;
  const chip = (
    <span className={`chip ${cls}`}>
      <span className="cdot" />
      {label}
    </span>
  );
  if (!onChange) return chip;
  return (
    <Select value={v} onValueChange={(n) => onChange(n as TaskStatus)}>
      <SelectTrigger className="h-auto border-0 bg-transparent p-0 shadow-none focus:ring-0">
        <SelectValue asChild>{chip}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        {STATUS_OPTIONS.map((s) => (
          <SelectItem key={s} value={s}>
            {STATUS_LABELS[s]}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// ── Priority ────────────────────────────────────────────────────────

export type TaskPriority = "high" | "medium" | "low";

const PRIORITY_LABELS: Record<TaskPriority, string> = {
  high: "High",
  medium: "Medium",
  low: "Low",
};
const PRIORITY_CLS: Record<TaskPriority, string> = {
  high: "p-high",
  medium: "p-med",
  low: "p-low",
};

export function PriorityChip({
  value,
  onChange,
}: {
  value: TaskPriority | string | null;
  onChange?: (next: TaskPriority) => void;
}) {
  const v = (value ?? "medium") as TaskPriority;
  const cls = PRIORITY_CLS[v] ?? "p-med";
  const label = PRIORITY_LABELS[v] ?? v;
  const chip = (
    <span className={`chip ${cls}`}>
      <span className="cdot" />
      {label}
    </span>
  );
  if (!onChange) return chip;
  return (
    <Select value={v} onValueChange={(n) => onChange(n as TaskPriority)}>
      <SelectTrigger className="h-auto border-0 bg-transparent p-0 shadow-none focus:ring-0">
        <SelectValue asChild>{chip}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        {(["high", "medium", "low"] as TaskPriority[]).map((p) => (
          <SelectItem key={p} value={p}>
            {PRIORITY_LABELS[p]}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// ── Phase ───────────────────────────────────────────────────────────

export type ProjectPhase =
  | "pre_construction"
  | "design"
  | "construction"
  | "post_construction"
  | "completed";

const PHASE_LABELS: Record<ProjectPhase, string> = {
  pre_construction: "Pre-Construction",
  design: "Design",
  construction: "Construction",
  post_construction: "Close-out",
  completed: "Completed",
};
const PHASE_CLS: Record<ProjectPhase, string> = {
  pre_construction: "ph-precon",
  design: "ph-design",
  construction: "ph-constr",
  post_construction: "ph-closeout",
  completed: "ph-closeout",
};

const PHASE_OPTIONS: ProjectPhase[] = [
  "pre_construction",
  "design",
  "construction",
  "post_construction",
  "completed",
];

export function PhaseChip({
  value,
  onChange,
}: {
  value: ProjectPhase | string | null;
  onChange?: (next: ProjectPhase) => void;
}) {
  const v = (value ?? "construction") as ProjectPhase;
  const cls = PHASE_CLS[v] ?? "ph-constr";
  const label = PHASE_LABELS[v] ?? v;
  const chip = (
    <span className={`phase ${cls}`}>
      <span className="pb" />
      {label}
    </span>
  );
  if (!onChange) return chip;
  return (
    <Select value={v} onValueChange={(n) => onChange(n as ProjectPhase)}>
      <SelectTrigger className="h-auto border-0 bg-transparent p-0 shadow-none focus:ring-0">
        <SelectValue asChild>{chip}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        {PHASE_OPTIONS.map((p) => (
          <SelectItem key={p} value={p}>
            {PHASE_LABELS[p]}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// ── Avatar ──────────────────────────────────────────────────────────

const PALETTE = ["av-a", "av-b", "av-c", "av-d", "av-e", "av-f"] as const;

function hashToPalette(key: string | null | undefined): string {
  if (!key) return "av-f";
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length] ?? "av-f";
}

function initials(name: string | null | undefined): string {
  if (!name) return "·";
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts
    .map((p) => p.charAt(0))
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

export function TaskAvatar({
  name,
  size,
  keyHint,
}: {
  name: string | null;
  size?: "sm" | "md" | "lg";
  keyHint?: string | null;
}) {
  const cls = hashToPalette(keyHint ?? name);
  const sizeCls = size === "lg" ? "lg" : "";
  return (
    <span className={`av ${cls} ${sizeCls}`} role="img" aria-label={name ?? "Unassigned"}>
      {initials(name)}
    </span>
  );
}

// ── Utility: relative date ──────────────────────────────────────────

export function formatRelativeDate(dueIso: string | null): {
  label: string;
  rel: string;
  variant: "overdue" | "duesoon" | "none";
} | null {
  if (!dueIso) return null;
  const due = new Date(dueIso);
  if (Number.isNaN(due.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.round((due.getTime() - today.getTime()) / 86400000);
  let rel = "";
  if (diffDays === 0) rel = "Today";
  else if (diffDays === 1) rel = "Tomorrow";
  else if (diffDays === -1) rel = "Yesterday";
  else if (diffDays > 0 && diffDays <= 14) rel = `in ${diffDays}d`;
  else if (diffDays < 0 && diffDays >= -30) rel = `${-diffDays}d ago`;
  else rel = due.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const label = due.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const variant = diffDays < 0 ? "overdue" : diffDays <= 3 ? "duesoon" : "none";
  return { label, rel, variant };
}
